import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '@/components/admin/AdminNav';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Calendar, 
  DollarSign, 
  Users, 
  TrendingUp, 
  Clock,
  CheckCircle,
  AlertCircle,
  FileText
} from 'lucide-react';
import SEO from '@/components/SEO';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

interface Stats {
  totalBookings: number;
  todayBookings: number;
  weekBookings: number;
  monthBookings: number;
  paidBookings: number;
  pendingBookings: number;
  totalRevenue: number;
  pendingRevenue: number;
  serviceBreakdown: { service: string; count: number }[];
  paymentMethodBreakdown: { method: string; count: number }[];
  recentBookings: any[];
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState<Stats>({
    totalBookings: 0,
    todayBookings: 0,
    weekBookings: 0,
    monthBookings: 0,
    paidBookings: 0,
    pendingBookings: 0,
    totalRevenue: 0,
    pendingRevenue: 0,
    serviceBreakdown: [],
    paymentMethodBreakdown: [],
    recentBookings: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Fetch all bookings
      const { data: allBookings, error } = await supabase
        .from('bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const bookings = allBookings || [];
      const now = new Date();
      const todayStart = startOfDay(now);
      const weekAgo = subDays(now, 7);
      const monthAgo = subDays(now, 30);

      // Calculate stats
      const totalBookings = bookings.length;
      const todayBookings = bookings.filter(b => new Date(b.created_at) >= todayStart).length;
      const weekBookings = bookings.filter(b => new Date(b.created_at) >= weekAgo).length;
      const monthBookings = bookings.filter(b => new Date(b.created_at) >= monthAgo).length;
      
      const paidBookings = bookings.filter(b => b.payment_status === 'paid').length;
      const pendingBookings = bookings.filter(b => b.payment_status === 'pending').length;
      
      const totalRevenue = paidBookings * 150; // $150 per consultation
      const pendingRevenue = pendingBookings * 150;

      // Service breakdown
      const serviceCount = bookings.reduce((acc, b) => {
        acc[b.service] = (acc[b.service] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const serviceBreakdown = Object.entries(serviceCount)
        .map(([service, count]) => ({ service, count }))
        .sort((a, b) => b.count - a.count);

      // Payment method breakdown
      const paymentCount = bookings.reduce((acc, b) => {
        acc[b.payment_method] = (acc[b.payment_method] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const paymentMethodBreakdown = Object.entries(paymentCount)
        .map(([method, count]) => ({ method, count }))
        .sort((a, b) => b.count - a.count);

      // Recent bookings (last 5)
      const recentBookings = bookings.slice(0, 5);

      setStats({
        totalBookings,
        todayBookings,
        weekBookings,
        monthBookings,
        paidBookings,
        pendingBookings,
        totalRevenue,
        pendingRevenue,
        serviceBreakdown,
        paymentMethodBreakdown,
        recentBookings,
      });

    } catch (error: any) {
      console.error('Error fetching stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard statistics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ 
    title, 
    value, 
    icon: Icon, 
    description, 
    trend 
  }: { 
    title: string; 
    value: string | number; 
    icon: any; 
    description?: string;
    trend?: string;
  }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
        {trend && (
          <div className="flex items-center mt-2 text-xs text-green-600">
            <TrendingUp className="h-3 w-3 mr-1" />
            {trend}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Admin Dashboard"
        description="Booking statistics and analytics"
      />
      <div className="min-h-screen flex flex-col bg-background">
        <AdminNav />
        
        <main className="flex-1 py-8">
          <div className="container mx-auto px-4">
            {/* Header */}
            <div className="mb-8 flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
                <p className="text-muted-foreground">Overview of booking metrics and statistics</p>
              </div>
              <Button onClick={() => navigate('/admin/bookings')}>
                <FileText className="h-4 w-4 mr-2" />
                View All Bookings
              </Button>
            </div>

            {/* Stats Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
              <StatCard
                title="Total Bookings"
                value={stats.totalBookings}
                icon={Users}
                description="All time"
              />
              <StatCard
                title="Today's Bookings"
                value={stats.todayBookings}
                icon={Calendar}
                description={format(new Date(), 'MMMM dd, yyyy')}
              />
              <StatCard
                title="This Week"
                value={stats.weekBookings}
                icon={TrendingUp}
                description="Last 7 days"
              />
              <StatCard
                title="This Month"
                value={stats.monthBookings}
                icon={Clock}
                description="Last 30 days"
              />
            </div>

            {/* Revenue Cards */}
            <div className="grid gap-4 md:grid-cols-2 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    ${stats.totalRevenue.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    From {stats.paidBookings} paid consultations
                  </p>
                  <div className="flex items-center mt-2 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                    <span className="text-muted-foreground">Confirmed payments</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pending Revenue</CardTitle>
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600">
                    ${stats.pendingRevenue.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    From {stats.pendingBookings} pending payments
                  </p>
                  <div className="flex items-center mt-2 text-xs">
                    <Clock className="h-3 w-3 mr-1 text-yellow-600" />
                    <span className="text-muted-foreground">Awaiting confirmation</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Breakdown Charts */}
            <div className="grid gap-6 md:grid-cols-2 mb-8">
              {/* Service Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Bookings by Service</CardTitle>
                  <CardDescription>Distribution of consultation types</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {stats.serviceBreakdown.map((item, index) => (
                      <div key={index}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{item.service}</span>
                          <span className="text-sm text-muted-foreground">{item.count}</span>
                        </div>
                        <div className="w-full bg-secondary rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{
                              width: `${(item.count / stats.totalBookings) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Payment Methods</CardTitle>
                  <CardDescription>Preferred payment options</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {stats.paymentMethodBreakdown.map((item, index) => (
                      <div key={index}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{item.method}</span>
                          <span className="text-sm text-muted-foreground">
                            {item.count} ({Math.round((item.count / stats.totalBookings) * 100)}%)
                          </span>
                        </div>
                        <div className="w-full bg-secondary rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{
                              width: `${(item.count / stats.totalBookings) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Bookings */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Bookings</CardTitle>
                <CardDescription>Latest 5 consultation bookings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.recentBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate('/admin/bookings')}
                    >
                      <div className="flex-1">
                        <p className="font-medium">{booking.full_name}</p>
                        <p className="text-sm text-muted-foreground">{booking.service}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{booking.payment_method}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(booking.created_at), 'MMM dd, HH:mm')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AdminDashboard;
